import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { ClipboardCheck, CheckCircle2, Award, ArrowRight, ClipboardList } from "lucide-react";
import { format } from "date-fns";

const AVATAR_COLORS = ["#6d28d9", "#4f46e5", "#0e7490", "#b45309", "#15803d", "#be185d"];
const avatarColor = (s: string) => AVATAR_COLORS[[...(s || "?")].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initials = (name: string) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const chipCls = (kind: string) => {
  const base = "grid h-9 w-9 place-items-center rounded-[11px] ";
  switch (kind) {
    case "amber": return base + "gradient-warm text-white shadow";
    case "violet": return base + "bg-violet-100 text-violet-600";
    case "green": return base + "bg-green-100 text-green-600";
    default: return base + "bg-muted text-muted-foreground";
  }
};

const ACADashboard = () => {
  const { fullName } = useAuth();
  const [stats, setStats] = useState({ awaitingReview: 0, approved: 0, certified: 0 });
  const [queue, setQueue] = useState<any[]>([]);

  useEffect(() => {
    const fetchAll = async () => {
      const { count: awaitingReview } = await supabase.from("applications").select("*", { count: "exact", head: true }).eq("status", "sent_for_approval");
      const { count: approved } = await supabase.from("applications").select("*", { count: "exact", head: true }).in("status", ["approved", "paid" as any]);
      const { count: certified } = await supabase.from("applications").select("*", { count: "exact", head: true }).eq("status", "certified");
      setStats({ awaitingReview: awaitingReview || 0, approved: approved || 0, certified: certified || 0 });

      const { data: apps } = await supabase
        .from("applications")
        .select("id, full_name, email, qualification, created_at")
        .eq("status", "sent_for_approval")
        .order("created_at", { ascending: true })
        .limit(6);
      setQueue(apps || []);
    };
    fetchAll();
  }, []);

  const displayName = fullName?.trim() || "there";

  const kpis = [
    { label: "Awaiting Review", value: stats.awaitingReview, icon: <ClipboardCheck className="h-[18px] w-[18px]" />, chip: "amber", sub: stats.awaitingReview ? "Applications to review" : "All caught up", href: "/aca/review" },
    { label: "Approved / Paid", value: stats.approved, icon: <CheckCircle2 className="h-[18px] w-[18px]" />, chip: "green", sub: "Cleared for membership", href: "/aca/approved" },
    { label: "Certified", value: stats.certified, icon: <Award className="h-[18px] w-[18px]" />, chip: "violet", sub: "Members certified", href: "/aca/approved" },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#7450a9]">ACA Portal</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-foreground">Welcome back, {displayName}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Review and approve membership applications</p>
          </div>
          <Link
            to="/aca/review"
            className="inline-flex items-center gap-2 rounded-full gradient-warm px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-violet-500/30 transition hover:opacity-95"
          >
            <ClipboardCheck className="h-4 w-4" /> Review Queue
          </Link>
        </div>

        {/* KPIs */}
        <div className="grid gap-4 sm:grid-cols-3">
          {kpis.map((k) => (
            <Link key={k.label} to={k.href} className="glass-panel rounded-2xl p-5 transition hover:-translate-y-0.5 hover:shadow-lg">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-medium text-muted-foreground">{k.label}</span>
                <span className={chipCls(k.chip)}>{k.icon}</span>
              </div>
              <p className="mt-3 text-[34px] font-bold leading-none tracking-tight tabular-nums text-foreground">{k.value}</p>
              <p className="mt-1.5 text-xs text-muted-foreground">{k.sub}</p>
            </Link>
          ))}
        </div>

        {/* Main */}
        <div className="grid gap-4 lg:grid-cols-[1.7fr_1fr]">
          {/* Awaiting review */}
          <div className="glass-panel rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h3 className="text-sm font-bold">Awaiting Your Review</h3>
              <Link to="/aca/review" className="text-xs font-semibold text-[#7450a9] hover:underline">View all →</Link>
            </div>
            {queue.length === 0 ? (
              <p className="px-5 py-12 text-center text-sm text-muted-foreground">Nothing awaiting review</p>
            ) : (
              queue.map((a) => (
                <Link
                  key={a.id}
                  to="/aca/review"
                  className="grid grid-cols-[1fr_auto] items-center gap-3 border-b border-border px-5 py-3 transition hover:bg-muted/40 last:border-0"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-xs font-bold text-white" style={{ background: avatarColor(a.full_name || a.email || "?") }}>
                      {initials(a.full_name || a.email)}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[13px] font-semibold text-foreground">{a.full_name || "—"}</p>
                      <p className="truncate text-xs text-muted-foreground">{a.qualification || a.email}</p>
                    </div>
                  </div>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">{a.created_at ? format(new Date(a.created_at), "MMM d") : ""}</span>
                </Link>
              ))
            )}
          </div>

          {/* Quick actions */}
          <div className="glass-panel rounded-2xl overflow-hidden self-start">
            <div className="border-b border-border px-5 py-4"><h3 className="text-sm font-bold">Quick Actions</h3></div>
            <div className="grid gap-2.5 p-3.5">
              <Link to="/aca/review" className="group flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 transition hover:bg-muted">
                <span className="grid h-9 w-9 place-items-center rounded-[11px] gradient-warm text-white shadow"><ClipboardList className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold">Review Applications</p>
                  <p className="text-xs text-muted-foreground">Approve or reject pending</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition group-hover:translate-x-1" />
              </Link>
              <Link to="/aca/approved" className="group flex items-center gap-3 rounded-xl border border-border bg-muted/40 p-3 transition hover:bg-muted">
                <span className="grid h-9 w-9 place-items-center rounded-[11px] gradient-warm text-white shadow"><CheckCircle2 className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold">Approved Members</p>
                  <p className="text-xs text-muted-foreground">View approved &amp; certified</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground transition group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ACADashboard;
